import { ApiODINMessageTypeConcurrentUser } from "../TApiODINMessageType";
import type { TApiODINMessageExtractionScreenSession } from "./TApiResponseExtraction";

type TResourceKey = "extraction-mapping" | "coa-remapping";

export const createApiScreenSessions = ({
    getAccessToken,
    baseWsUrl,
}: {
    getAccessToken: () => Promise<string>;
    baseWsUrl: string;
}) => ({
    startScreenSession: async ({
        extractionId,
        resourceKey,
        onConnectionOpened,
        onConnectionClosed,
        onActive,
    }: {
        extractionId: number;
        resourceKey: TResourceKey;
        onConnectionOpened?: () => void;
        onConnectionClosed?: () => void;
        onActive?: (params: { activeUsers: string[] }) => void;
    }): Promise<() => void> => {
        const resourceId = extractionId;

        const accessToken = await getAccessToken();
        let ws: WebSocket | null = null;
        let heartbeatIntervalId: number | null = null;

        const cleanup = () => {
            if (heartbeatIntervalId !== null) {
                clearInterval(heartbeatIntervalId);
                heartbeatIntervalId = null;
            }
            if (ws) {
                ws.close();
                ws = null;
            }
        };

        const connect = () => {
            cleanup();

            ws = new WebSocket(
                `${baseWsUrl}/ws/screen-session/${resourceKey}/${resourceId}`,
            );

            ws.addEventListener("open", () => {
                ws?.send(accessToken);

                if (ws?.readyState === WebSocket.OPEN) {
                    ws.send("ping");
                }

                heartbeatIntervalId = window.setInterval(() => {
                    if (ws?.readyState === WebSocket.OPEN) {
                        ws.send("ping");
                    }
                }, 15000);

                onConnectionOpened?.();
            });

            ws.addEventListener("close", () => {
                cleanup();
                onConnectionClosed?.();
            });

            ws.addEventListener("message", (event: MessageEvent<string>) => {
                const message = JSON.parse(
                    event.data,
                ) as TApiODINMessageExtractionScreenSession;

                if (
                    message.type ===
                    ApiODINMessageTypeConcurrentUser.ACTIVE_USER
                ) {
                    onActive?.({ activeUsers: message.contents });
                }

                if (
                    message.type === ApiODINMessageTypeConcurrentUser.HEARTBEAT
                ) {
                    if (message.contents === "ping") {
                        ws?.send("pong");
                    }
                }
            });
        };

        connect();

        return cleanup;
    },
});
